/**
 * The Operations Hub map (Leaflet 1.9.4; docs/specs/portal/operations-hub.md §14, §18). It draws what the domain gives
 * it: each project's outline (block boundaries, else its contract's survey areas) in the boundary blue of every map
 * (decision 658; the aircraft type's colour is on the pin and the progress bar), one pin per project with its popup,
 * each line in its Line review state from zoom 10 in for the projects in view (one canvas renderer, built per project
 * once and again when the base map changes), and the organisation's tracked aircraft and public air traffic with
 * their trails (decision 420; drawn as Teslyn Field draws them, `aircraftLayer.ts`). Every line and outline is drawn
 * over its thin dark edge; the box carries `data-basemap`, so the base-map
 * colours follow the base map shown (decision 654). Colours are tokens read from the CSS variables at runtime; text
 * reaches the map only as text nodes.
 */
import { useEffect, useRef, type ReactElement } from 'react';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import {
  baseMapKind,
  hubShowsLines,
  unionBounds,
  type AircraftColours,
  type AircraftPosition,
  type BaseLayerId,
  type GeoBounds,
  type HubProject,
  type MapFeature,
  type TrailPoint,
} from '@teslyn/domain';
import { drawAircraft } from '../map/aircraftLayer.ts';
import { RouteOverlay, type RouteFetcher } from '../map/publicRoute.ts';
import { firePopup } from '../map/cloudFires.tsx';
import { baseLayer, css, elementCss, shapesOf, textNode, type Css } from '../map/leaflet.ts';

export interface HubMapTracking {
  readonly aircraft: readonly AircraftPosition[];
  readonly trails: ReadonlyMap<string, readonly TrailPoint[]>;
  readonly colours: ReadonlyMap<string, AircraftColours>;
  /** The public traffic services' credits while their aircraft are drawn ("Traffic: adsb.lol (ODbL)", parity C7). */
  readonly credits?: readonly string[];
}

export interface HubMapProps {
  /** The projects shown (the panel's filter applied). */
  readonly projects: readonly HubProject[];
  /** A project's line-state features once its plan is loaded; null before. */
  readonly linesOf: (projectId: string) => readonly MapFeature[] | null;
  /** The popup of a project's pin (text nodes only). */
  readonly popupFor: (p: HubProject) => HTMLElement;
  readonly base: BaseLayerId;
  /** "Locate on map": the box to show, with a counter so the same project can be located twice. */
  readonly focus: { readonly box: GeoBounds; readonly nonce: number } | null;
  readonly tracking: HubMapTracking | null;
  /** The aircraft the map keeps in view (Follow); dragging the map stops it. */
  readonly follow: string | null;
  readonly onFollowStopped: () => void;
  /** Fly to one aircraft (pressing its name in Tracking assets). */
  readonly flyTo: { readonly id: string; readonly nonce: number } | null;
  readonly onZoom: (zoom: number) => void;
  readonly label: string;
  /** The fire layers ticked (decision 806): hot spots then the agencies' fires, over the outlines, under the pins. */
  readonly fires?: readonly MapFeature[];
  /** Their sources' credits while drawn (each agency's own words, DEA's). */
  readonly fireCredits?: readonly string[];
  /** A click on a public aircraft asks its planned route (R160, staff only); none: no route. */
  readonly routeOf?: RouteFetcher;
}

const toLatLng = (b: GeoBounds): L.LatLngBoundsExpression => [
  [b.south, b.west],
  [b.north, b.east],
];

/** The paths of features, bottom first: every edge under every line (decision 654), then the shapes with `each`. */
function addShapes(
  group: L.LayerGroup,
  features: readonly MapFeature[],
  read: Css,
  each: (f: MapFeature, shape: L.Path | L.Marker) => void,
  renderer?: L.Renderer,
): void {
  const shapes = features.map((f) => ({ f, ...shapesOf(f, read, renderer ? { renderer } : {}) }));
  for (const s of shapes) for (const u of s.under) group.addLayer(u);
  for (const { f, main } of shapes) {
    each(f, main);
    group.addLayer(main);
  }
}

export function HubMap(props: HubMapProps): ReactElement {
  const host = useRef<HTMLDivElement>(null);
  const map = useRef<L.Map | null>(null);
  const layers = useRef<{
    base: L.LayerGroup | null;
    outlines: L.LayerGroup;
    lines: L.LayerGroup;
    fires: L.LayerGroup;
    pins: L.LayerGroup;
    trails: L.LayerGroup;
    aircraft: L.LayerGroup;
    route: RouteOverlay;
    canvas: L.Canvas;
  } | null>(null);
  // line layers per project, built once from the features they were built from
  const lineCache = useRef(new Map<string, { from: readonly MapFeature[]; layer: L.LayerGroup }>());
  const fitted = useRef(false);
  const latest = useRef(props);
  latest.current = props;

  // ---- the map
  useEffect(() => {
    if (!host.current) return undefined;
    // no zoom, fade or pan animation for people who ask for less motion
    const still =
      typeof window.matchMedia === 'function' &&
      window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    const m = L.map(host.current, {
      zoomControl: true,
      attributionControl: true,
      zoomAnimation: !still,
      fadeAnimation: !still,
      markerZoomAnimation: !still,
    });
    const canvas = L.canvas({ padding: 0.5 });
    layers.current = {
      base: null,
      outlines: L.layerGroup().addTo(m),
      lines: L.layerGroup().addTo(m),
      fires: L.layerGroup().addTo(m),
      pins: L.layerGroup().addTo(m),
      trails: L.layerGroup().addTo(m),
      route: new RouteOverlay(m, L.layerGroup().addTo(m), () => -new Date().getTimezoneOffset() / 60),
      aircraft: L.layerGroup().addTo(m),
      canvas,
    };
    m.setView([-25, 134], 4); // Australia, until the projects are in
    fitted.current = false; // a new map (React may make it twice in development) fits the projects again
    latest.current.onZoom(4);
    map.current = m;
    const refresh = (): void => {
      latest.current.onZoom(m.getZoom());
      drawLines();
    };
    m.on('moveend zoomend', refresh);
    m.on('dragstart', () => {
      if (latest.current.follow) latest.current.onFollowStopped();
    });
    const resize =
      typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => m.invalidateSize());
    resize?.observe(host.current);
    const cache = lineCache.current;
    return () => {
      resize?.disconnect();
      m.remove();
      map.current = null;
      layers.current = null;
      cache.clear();
    };
  }, []);

  // ---- the base map
  useEffect(() => {
    const m = map.current;
    const l = layers.current;
    if (!m || !l) return;
    l.base?.remove();
    l.base = baseLayer(props.base).addTo(m);
    l.base.eachLayer((t) => (t as L.TileLayer).bringToBack());
    // the lines take the new base map's colours: build them again (decision 654)
    for (const entry of lineCache.current.values()) entry.layer.remove();
    lineCache.current.clear();
    drawLines();
  }, [props.base]);

  // ---- the lines of the projects in view, from zoom 10 in (§16.5)
  function drawLines(): void {
    const m = map.current;
    const l = layers.current;
    if (!m || !l) return;
    const zoom = m.getZoom();
    const b = m.getBounds();
    const view = { south: b.getSouth(), west: b.getWest(), north: b.getNorth(), east: b.getEast() };
    const wanted = new Set<string>();
    for (const p of latest.current.projects) {
      if (!hubShowsLines(zoom, p.box, view)) continue;
      const features = latest.current.linesOf(p.id);
      if (!features) continue;
      wanted.add(p.id);
      let entry = lineCache.current.get(p.id);
      if (!entry || entry.from !== features) {
        entry?.layer.remove();
        const group = L.layerGroup();
        addShapes(
          group,
          features,
          elementCss(host.current),
          (f, s) => s.bindTooltip(textNode(f.tooltip), { sticky: true }),
          l.canvas,
        );
        entry = { from: features, layer: group };
        lineCache.current.set(p.id, entry);
      }
      if (!l.lines.hasLayer(entry.layer)) l.lines.addLayer(entry.layer);
    }
    for (const [id, entry] of lineCache.current) {
      if (!wanted.has(id) && l.lines.hasLayer(entry.layer)) l.lines.removeLayer(entry.layer);
    }
  }

  // ---- outlines and pins
  useEffect(() => {
    const m = map.current;
    const l = layers.current;
    if (!m || !l) return;
    l.outlines.clearLayers();
    l.pins.clearLayers();
    const pinPx = Number.parseFloat(css('map-hub-pin-size')) || 20;
    const read = elementCss(host.current);
    addShapes(
      l.outlines,
      props.projects.flatMap((p) => p.outline.map((f) => ({ ...f, tooltip: `${p.number} · ${f.tooltip}` }))),
      read,
      (f, s) => s.bindTooltip(textNode(f.tooltip), { sticky: true }),
    );
    for (const p of props.projects) {
      if (p.pin) {
        const pin = L.marker([p.pin.lat, p.pin.lng], {
          title: `${p.number} ${p.title}`,
          alt: `${p.number} ${p.title}`,
          keyboard: true,
          icon: L.divIcon({
            className: `hub-pin hub-pin--${p.aircraft.type}`,
            html: '',
            iconSize: [pinPx, pinPx],
          }),
        });
        pin.bindPopup(() => latest.current.popupFor(p), { maxWidth: 320 });
        l.pins.addLayer(pin);
      }
    }
    if (!fitted.current) {
      const box = unionBounds(props.projects.map((p) => p.box));
      if (box) {
        m.invalidateSize(); // the column may have been laid out after the map was made
        m.fitBounds(toLatLng(box), { padding: [24, 24], maxZoom: 11 });
        fitted.current = true;
      }
    }
    drawLines();
  }, [props.projects, props.linesOf]);

  // ---- the fire layers (decision 806): information only; each popup says so
  useEffect(() => {
    const l = layers.current;
    if (!l) return;
    l.fires.clearLayers();
    addShapes(
      l.fires,
      props.fires ?? [],
      elementCss(host.current),
      (f, s) => {
        s.bindTooltip(textNode(f.tooltip), { sticky: true });
        s.bindPopup(() => firePopup(f), { maxWidth: 360 });
      },
      l.canvas,
    );
  }, [props.fires, props.base]);

  // ---- Locate on map
  useEffect(() => {
    const m = map.current;
    if (!m || !props.focus) return;
    m.invalidateSize();
    m.fitBounds(toLatLng(props.focus.box), { padding: [24, 24], maxZoom: 13 });
  }, [props.focus]);

  // ---- tracked aircraft and their trails
  useEffect(() => {
    const m = map.current;
    const l = layers.current;
    if (!m || !l) return;
    l.trails.clearLayers();
    l.aircraft.clearLayers();
    const t = props.tracking;
    if (!t) return;
    drawAircraft(l.trails, l.aircraft, t, {
      offsetH: -new Date().getTimezoneOffset() / 60,
      renderer: l.canvas,
      // a public aircraft's planned route, asked once per click (R160)
      onPublicClick: (a) => {
        const f = latest.current.routeOf;
        if (f) l.route.open(a, f, t.colours);
      },
    });
    const followed = props.follow ? t.aircraft.find((a) => a.id === props.follow) : undefined;
    if (followed) m.panTo([followed.lat, followed.lng]);
  }, [props.tracking, props.follow]);

  // ---- the public traffic services' credits, as their terms ask (adsb.lol's ODbL, adsb.fi's link)
  const credited = useRef<readonly string[]>([]);
  useEffect(() => {
    const control = map.current?.attributionControl;
    if (!control) return;
    const next = [...(props.tracking?.credits ?? []), ...(props.fireCredits ?? [])];
    for (const c of credited.current) if (!next.includes(c)) control.removeAttribution(c);
    for (const c of next) if (!credited.current.includes(c)) control.addAttribution(c);
    credited.current = next;
  }, [props.tracking, props.fireCredits]);

  // ---- fly to an aircraft
  useEffect(() => {
    const m = map.current;
    const a = props.flyTo ? latest.current.tracking?.aircraft.find((x) => x.id === props.flyTo!.id) : null;
    if (!m || !a) return;
    m.flyTo([a.lat, a.lng], Math.max(m.getZoom(), 11));
  }, [props.flyTo]);

  return (
    <div
      ref={host}
      className="hub-map"
      role="region"
      aria-label={props.label}
      data-basemap={baseMapKind(props.base)}
    />
  );
}
